/* eslint-disable react/forbid-prop-types */
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { postData, globalAccountsView } from '../../redux/reducers/data';
import { setMessage } from '../../redux/actions/message';
import {
  parseTags,
  fetchLoanRepaymentSplitFromDescription,
} from './loanRepaymentSplitHelpers';
import { MonestrySpinner as Spinner } from '../../icons/remoteAssets';

function buildEligibleLoanAccountsByDepositAccount(allAccounts = []) {
  const map = new Map();
  allAccounts
    .filter((account) => account.category === 'loans')
    .forEach((loanAccount) => {
      (loanAccount.connectedDepositAccounts || []).forEach((depositAccountId) => {
        const list = map.get(depositAccountId) || [];
        list.push(loanAccount);
        map.set(depositAccountId, list);
      });
    });
  return map;
}

export default function TransactionContextAiLoanRepayment({ transactions, category, signalHandleSubmit, setIsActionRunning }) {
  const { t } = useTranslation('accountDetails', { keyPrefix: 'splitMenu' });
  const dispatch = useDispatch();
  const allAccounts = useSelector(globalAccountsView) || [];
  const [isRunning, setIsRunning] = useState(false);

  const loansByDepositAccount = useMemo(() => buildEligibleLoanAccountsByDepositAccount(allAccounts), [allAccounts]);

  useEffect(() => {
    setIsActionRunning(isRunning);
  }, [isRunning, setIsActionRunning]);

  useEffect(() => {
    async function run() {
      if (!signalHandleSubmit || isRunning || category !== 'deposits' || !transactions?.length) return;

      setIsRunning(true);

      try {
        const successfulUpdates = [];
        let hadFailure = false;

        // Run AI in parallel for better UX on multi-select.
        const splitResults = await Promise.all(
          transactions.map(async (transaction) => {
            try {
              const split = await fetchLoanRepaymentSplitFromDescription(transaction?.description || '');
              return { transaction, split };
            } catch (error) {
              return { transaction, split: null };
            }
          }),
        );

        splitResults.forEach(({ transaction, split }) => {
          if (!split) {
            hadFailure = true;
            return;
          }

          const tags = parseTags(transaction?.tags);
          const connectedLoanAccounts = loansByDepositAccount.get(transaction.accountId) || [];

          let resolvedLoanAccountId = tags?.accountId || null;
          const hasResolvedLoan = resolvedLoanAccountId && allAccounts.some((acc) => acc.id === resolvedLoanAccountId && acc.category === 'loans');

          if (!hasResolvedLoan) {
            resolvedLoanAccountId = connectedLoanAccounts[0]?.id || null;
          }

          if (!resolvedLoanAccountId) {
            hadFailure = true;
            return;
          }

          delete tags.assetId;

          successfulUpdates.push({
            ...transaction,
            label: 'investment-loanRepayment',
            tags: {
              ...tags,
              accountId: resolvedLoanAccountId,
              loanRepayment: {
                principal: Math.abs(split.principal),
                interest: Math.abs(split.interest),
              },
            },
            linkedTransactions: (transaction.linkedTransactions || []).filter(
              (linkedTransaction) => !['fee-to-transaction', 'sale-to-transaction', 'transfer'].includes(linkedTransaction.tags?.linkType),
            ),
            importFlag: 'put',
            parentId: null,
          });
        });

        if (successfulUpdates.length > 0) {
          const updatesByAccount = successfulUpdates.reduce((acc, item) => {
            const existing = acc[item.accountId] || [];
            existing.push(item);
            acc[item.accountId] = existing;
            return acc;
          }, {});

          await Promise.all(
            Object.entries(updatesByAccount).map(([txnAccountId, data]) => dispatch(
              postData({
                data,
                category: 'deposits',
                accountId: txnAccountId,
              }),
            )),
          );

          if (hadFailure) {
            dispatch(setMessage('unableToUpdateAllTransactions'));
          }

          window.dispatchEvent(new CustomEvent('setDialog', {}));
          return;
        }

        dispatch(setMessage('unableToUpdateAllTransactions'));
      } catch (error) {
        dispatch(setMessage('unableToUpdateAllTransactions'));
      } finally {
        setIsRunning(false);
      }
    }

    run();
  }, [signalHandleSubmit, isRunning, category, transactions, loansByDepositAccount, allAccounts, dispatch]);

  return (
    <div className="mt-2">
      <p className="text-sm text-gray-500">{t('aiLabelLoanRepaymentInfo')}</p>
      {isRunning && (
        <div className="mt-2 flex items-start gap-2" data-testid="ai-loan-repayment-running">
          <Spinner width="18" height="18" viewBox="0 0 600 600" />
          <div>
            <p className="text-sm text-gray-400">{t('aiLabelLoanRepaymentRunning')}</p>
            <p className="text-xs text-gray-400">{t('aiLabelLoanRepaymentRunningTakesUpTo30s')}</p>
          </div>
        </div>
      )}
    </div>
  );
}

TransactionContextAiLoanRepayment.propTypes = {
  category: PropTypes.string.isRequired,
  signalHandleSubmit: PropTypes.bool.isRequired,
  setIsActionRunning: PropTypes.func,
  transactions: PropTypes.arrayOf(PropTypes.object).isRequired,
};
TransactionContextAiLoanRepayment.defaultProps = {
  setIsActionRunning: () => {},
};
